import { useCallback, useSyncExternalStore } from 'react';

/** Whether `query` matches now; `fallback` where matchMedia is unavailable. */
function matches(query: string, fallback: boolean): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
  return window.matchMedia(query).matches;
}

/**
 * Subscribes to a CSS media query. `fallback` is the answer where the browser
 * (or the test environment) has no matchMedia.
 */
export function useMediaQuery(query: string, fallback = false): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
      }
      const list = window.matchMedia(query);
      list.addEventListener('change', notify);
      return () => {
        list.removeEventListener('change', notify);
      };
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => matches(query, fallback),
    () => fallback,
  );
}

/** Tailwind's md breakpoint: tablet and desktop. */
export const MD_QUERY = '(min-width: 768px)';
/** A pointer that can hover (mouse, trackpad), not touch only. */
export const HOVER_QUERY = '(hover: hover)';
